/**
 * Everything about how to run a chosen action, as a form states it.
 *
 * Shared by the trigger form's invoke-action target and, once it is rebuilt,
 * the manual "Invoke action" form: both collect the same inputs, parameters,
 * overrides and output destination, and differ only in what surrounds them.
 * Nothing here knows about triggers -- a target's identity, and the file
 * patterns a change gates on, live beside these values, not in them.
 *
 * Every rule about these values is attached here, so the second form inherits
 * them rather than restating them.
 */

import { z } from "zod";

import {
  actionStorageMax,
  actionStorageMin,
  isActionStorageValid,
} from "../components/action/actionUtils";

/**
 * A picker that starts empty: null is a legal draft, and a problem once the
 * form is submitted. The parsed output is non-null, so what reaches a request
 * builder needs no further checking.
 */
export function required<T extends z.ZodType>(schema: T, message: string) {
  return schema.nullable().refine((value) => value !== null, message);
}

/**
 * File patterns as they were typed. A blank tag is noise someone left behind
 * rather than something to make them fix, so the parsed value drops it and the
 * builders never have to; a pattern is trimmed for the same reason.
 */
export const filePatternsSchema = z
  .array(z.string())
  .transform((patterns) =>
    patterns
      .map((pattern) => pattern.trim())
      .filter((pattern) => pattern.length > 0),
  );

/** One environment-variable row; an object because `useFieldArray` keys rows by identity. */
export const envVarRowSchema = z.object({
  key: z.string(),
  value: z.string(),
});

/**
 * The action to invoke, as a target stores it. `owner` defaults to the
 * invoking org and `digest` to the action's latest version; both are carried
 * through unedited so a pinned version survives a round trip.
 */
const actionReferenceSchema = z.object({
  name: z.string().min(1),
  owner: z.string().nullable(),
  digest: z.string().nullable(),
});

/**
 * One source of inputs beyond whatever the invocation is already handed: a
 * dataset with optional file patterns, or a query resolved when it runs. Both
 * become one `files` selector; the form writes no topic or session selector.
 */
/**
 * The dataset a firing is about, as a selector spells it. The runtime resolves
 * `{{ placeholders }}` in an invocation input against the event before the
 * invocation is created, so a selector can name the dataset that fired rather
 * than one picked when the trigger was written.
 *
 * Resolution is strict: a placeholder that names nothing the event exposes
 * fails the dispatch, so only a firing that carries a dataset may use this.
 */
export const TRIGGERING_DATASET_ID = "{{ dataset.dataset_id }}";

const inputSourceSchema = z.discriminatedUnion("source", [
  z.object({
    source: z.literal("dataset"),
    datasetId: required(z.string(), "Pick a dataset."),
    globs: filePatternsSchema,
  }),
  z.object({
    source: z.literal("triggering-dataset"),
    globs: filePatternsSchema,
  }),
  z.object({
    source: z.literal("query"),
    text: z.string().trim().min(1, "Write a query, or remove this input."),
  }),
]);

/**
 * Overrides applied to the action's declared compute and container settings.
 *
 * `storage` is bounded by the platform maximum; an org on a lower tier is held
 * to its own limit by the control that offers the value, which is the only
 * place that limit is known.
 */
const overridesSchema = z.object({
  cpu: z.string().optional(),
  memory: z.string().optional(),
  gpu: z.string().optional(),
  storage: z
    .number()
    .refine((storage) => isActionStorageValid(storage), {
      message: `Storage must be between ${actionStorageMin} GiB and ${actionStorageMax} GiB. Contact support@roboto.ai for higher amounts.`,
    })
    .optional(),
  timeout: z.number().nullable().optional(),
  workdir: z.string().optional(),
  command: z.string().optional(),
  entrypoint: z.string().optional(),
  envVars: z.array(envVarRowSchema),
});

/**
 * The invocation block both the trigger form and the manual invoke form nest
 * under one key, so a sub-form can be handed the path it lives at.
 */
export const invocationSpecSchema = z.object({
  action: required(actionReferenceSchema, "Select an action to invoke."),
  /**
   * Parameter name to whatever JSON the wire stores, carried through as it was
   * stored: the runtime resolves `{{ path }}` placeholders in the string leaves
   * of a value, so a nested object is a value this form must not flatten. Only
   * values someone set are here, so a parameter left alone follows the action's
   * declared default whenever the invocation runs.
   */
  parameters: z.record(z.string(), z.unknown()),
  /** What the invocation gets beyond its subject, in the order the rows were added. */
  inputs: z.array(inputSourceSchema),
  /** Where outputs are uploaded. "source" leaves it to the runtime's own default. */
  outputDestination: z
    .object({
      mode: z.enum(["source", "dataset"]),
      datasetId: z.string().nullable(),
    })
    .refine(
      (destination) =>
        destination.mode !== "dataset" || destination.datasetId !== null,
      {
        path: ["datasetId"],
        message: "Pick the dataset the output is uploaded to.",
      },
    ),
  overrides: overridesSchema,
});

export type InvocationSpecValues = z.input<typeof invocationSpecSchema>;
export type InvocationSpecOutput = z.output<typeof invocationSpecSchema>;
export type InputSourceValues = z.input<typeof inputSourceSchema>;

/** A fresh, empty invocation block. */
export function defaultInvocationSpecValues(): InvocationSpecValues {
  return {
    action: null,
    parameters: {},
    inputs: [],
    outputDestination: { mode: "source", datasetId: null },
    overrides: { envVars: [] },
  };
}
